import React, { use } from "react";
import { useAtom } from "jotai";
import ScreenCard from "../ScreenCard";
import { newMissionState } from "../../atoms/core";
import MissionCCT from "../../components/editors/MissionCCT";
import { CreateMissionScreensContext } from "../../contexts/CreateMissionManager";
import { useI18n } from "../../hooks/i18n-hooks";
import { Cct, Mission } from "../../__generated__/graphql";
import { CctDetailsFieldFullValueFieldsFragment } from "../../__generated__/operations/graphql";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { Bubble } from "../../components/Bubble";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";
import MarkdownView from "../../components/MarkdownView";
import { useIntercom } from "../../hooks/intercom";
import VStack from "../../ui_atoms/VStack";
import { useTheme } from "../../hooks/use-theme";
import { getIjmSelection } from "../../utils/ijm-utils";
import { useGetHoursPerWeekAndWorkingTime } from "../../hooks/cct-utils";

const CreateMissionCCT = () => {
    const i18n = useI18n();
    const [mission, setMission] = useAtom(newMissionState);
    const currentProfile = useCurrentProfile();
    const { fonts, colors } = useTheme();
    const isPrivateClient = currentProfile?.isPrivateClient || false;
    const { validateScreen } = use(CreateMissionScreensContext);
    const [isValid, setIsValid] = React.useState(false);
    const getHoursPerWeekAndWorkingTime = useGetHoursPerWeekAndWorkingTime();
    const setCCTId = React.useCallback(
        (value?: Cct) => {
            setMission((oldMission) => {
                if (value) {
                    const { hoursPerWeekFullTime, workingTimeClassicSpec } =
                        getHoursPerWeekAndWorkingTime(oldMission as Mission, value);
                    return {
                        ...oldMission,
                        currentContract: {
                            ...oldMission.currentContract,
                            cctEditionId: value?.cctEditionId,
                            cctContractId: value?.cctContractId,
                            cctDetails: { readableValues: [], tempdataCallValues: [] },
                            hoursPerWeekFullTime,
                            workingTimeClassicSpec,
                        },
                        ijmSelection:
                            currentProfile?.client && value
                                ? getIjmSelection(currentProfile?.client, value, "all")
                                : undefined,
                    };
                } else {
                    return {
                        ...oldMission,
                        currentContract: {
                            ...oldMission.currentContract,
                            cctEditionId: undefined,
                            cctContractId: undefined,
                        },
                    };
                }
            });
        },
        [setMission, currentProfile?.client, getHoursPerWeekAndWorkingTime],
    );

    const setCctDetails = React.useCallback(
        (value: CctDetailsFieldFullValueFieldsFragment) => {
            setMission((oldMission) => ({
                ...oldMission,
                currentContract: {
                    ...oldMission.currentContract,
                    cctDetails: value,
                },
            }));
        },
        [setMission],
    );

    const setScreenIsValid = React.useCallback(
        (value: boolean) => {
            setIsValid(value);
        },
        [setIsValid],
    );

    const validate = React.useCallback(() => {
        void validateScreen("CreateMissionCCT");
    }, [validateScreen]);

    const origin = useBubbleStepOrigin("CreateMissionCCT");

    const { presentMessageComposer } = useIntercom();
    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.translate("next"),
                    onPress: validate,
                    isValid: isValid,
                },
            ]}
            withAssistanceButton
        >
            <Bubble
                title={
                    !isPrivateClient
                        ? i18n.t("screens.user.create_mission_cct.title")
                        : i18n.t("screens.user.create_mission_cct.private_client_title")
                }
                origin={origin}
            >
                <MissionCCT
                    mission={mission}
                    setCCTId={setCCTId}
                    setCctDetails={setCctDetails}
                    setScreenIsValid={setScreenIsValid}
                />
            </Bubble>
            <VStack h="2/3" v="1/3">
                <MarkdownView
                    onLinkPress={() => {
                        void presentMessageComposer();
                        return false;
                    }}
                    markdownStyle={{
                        paragraph: {
                            color: colors.greyAccessible,
                            ...fonts.bodySmall,
                        },
                    }}
                    markdown={i18n.t("screens.user.create_mission_cct_details.contact_assistance")}
                />
            </VStack>
        </ScreenCard>
    );
};

export default CreateMissionCCT;
